import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useAuthContext } from '@/context/AuthProvider';
import { UserAttribute } from '@/models/User';
import { UpdateUserProfile, UserProfile } from '@/models/UserProfile';
import {
  getUserProfile,
  updateUserProfile as updateUserProfileEndpoint,
} from '@/services/api/users';

export const useUserProfile = () => {
  const queryClient = useQueryClient();
  const [isUpdatingUserProfile, setIsUpdatingUserProfile] = useState(false);
  const [isUserProfileUpdated, setIsUserProfileUpdated] = useState(false);
  const [updateUserProfileError, setUpdateUserProfileError] = useState<string | null>(null);

  const { updateUserAttributes } = useAuthContext();

  const { data, refetch, isLoading, isFetching } = useQuery<UserProfile>({
    queryKey: ['user-profile'],
    queryFn: getUserProfile,
    refetchOnWindowFocus: false,
  });

  const { mutateAsync } = useMutation<UserProfile, Error, UpdateUserProfile>({
    mutationFn: updateUserProfileEndpoint,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });

  const updateUserProfile = async (
    data: UpdateUserProfile,
    params?: { markUserProfileAsComplete: boolean },
  ) => {
    setIsUpdatingUserProfile(true);
    setUpdateUserProfileError(null);

    try {
      await mutateAsync(data);

      const hasNameUpdate = data.firstName !== undefined || data.lastName !== undefined;
      const needsCognitoUpdate = hasNameUpdate || params?.markUserProfileAsComplete;

      if (needsCognitoUpdate) {
        const userAttributesToUpdate: Partial<Record<UserAttribute, string>> = {};
        if (data.firstName !== undefined) {
          userAttributesToUpdate[UserAttribute.givenName] = data.firstName;
        }
        if (data.lastName !== undefined) {
          userAttributesToUpdate[UserAttribute.familyName] = data.lastName;
        }
        if (hasNameUpdate) {
          userAttributesToUpdate[UserAttribute.name] = [data.firstName, data.lastName]
            .filter(Boolean)
            .join(' ');
        }
        if (params?.markUserProfileAsComplete) {
          userAttributesToUpdate[UserAttribute.isProfileComplete] = 'true';
        }
        await updateUserAttributes(userAttributesToUpdate);
      }

      setIsUserProfileUpdated(true);
    } catch (error: any) {
      setUpdateUserProfileError(error.message);
      setIsUserProfileUpdated(false);
    } finally {
      setIsUpdatingUserProfile(false);
    }
  };

  const updateNotificationPreferences = async (
    prefs: Pick<UpdateUserProfile, 'notificationFrequency' | 'commEmailEnabled' | 'marketingOptIn'>,
  ): Promise<boolean> => {
    setIsUpdatingUserProfile(true);
    setUpdateUserProfileError(null);
    try {
      await mutateAsync(prefs);
      setIsUserProfileUpdated(true);
      return true;
    } catch (error: any) {
      setUpdateUserProfileError(error.message);
      setIsUserProfileUpdated(false);
      return false;
    } finally {
      setIsUpdatingUserProfile(false);
    }
  };

  return {
    userProfile: data,
    refetchUserProfile: refetch,
    isFetchingUserProfile: isFetching,
    isInitialFetchingUserProfile: isLoading,
    updateUserProfile,
    updateNotificationPreferences,
    isUpdatingUserProfile,
    isUserProfileUpdated,
    updateUserProfileError,
  };
};
